import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Quote, Star, ExternalLink } from "lucide-react";

const TestimonialCard = ({ testimonial, isActive }) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: isActive ? 1 : 0.5, scale: isActive ? 1 : 0.95 }}
      transition={{ duration: 0.4 }}
      className={`bg-[#1A1F26] border border-[#30363D] rounded-2xl p-6 ${isActive ? 'border-[#007AFF]/30' : ''}`}
      data-testid={`testimonial-card-${testimonial.id}`}
    >
      {/* Quote Icon */}
      <div className="mb-6">
        <Quote className="w-10 h-10 text-[#007AFF]/30" />
      </div>

      {/* Content */}
      <p className="text-base text-[#8B949E] leading-relaxed mb-6 line-clamp-5">
        {testimonial.content}
      </p>

      {/* Project Info */}
      <div className="mb-4">
        <h4 className="font-semibold text-white">{testimonial.project_title}</h4>
        <p className="text-sm text-[#8B949E]">{testimonial.date_range}</p>
      </div>

      {/* Rating */}
      <div className="flex items-center gap-1 mb-4">
        {[...Array(5)].map((_, i) => (
          <Star 
            key={i} 
            className={`w-4 h-4 ${i < testimonial.rating ? 'text-yellow-400 fill-yellow-400' : 'text-[#30363D]'}`} 
          />
        ))}
      </div>

      {/* Source */}
      <div className="flex items-center gap-2 text-sm text-[#8B949E]">
        <span>Source:</span>
        <a 
          href="https://www.upwork.com" 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[#007AFF] hover:underline"
        >
          {testimonial.source}
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      {/* Tags */}
      <div className="flex flex-wrap gap-2 mt-4">
        {testimonial.tags?.map((tag) => (
          <span 
            key={tag} 
            className="px-3 py-1 rounded-full bg-[#007AFF]/10 text-[#007AFF] text-xs font-medium"
          >
            {tag}
          </span>
        ))}
      </div>
    </motion.div>
  );
};

const Testimonials = ({ testimonials }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section id="testimonials" className="py-20 md:py-28 bg-[#1A1F26]" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-sm text-[#007AFF] font-medium uppercase tracking-wider">Testimonials</span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mt-4 mb-6">
            What Clients Say About <span className="gradient-text">Working With Us</span>
          </h2>
          <p className="text-[#8B949E] max-w-2xl mx-auto text-lg">
            Don't just take our word for it - hear from clients who've experienced measurable growth.
          </p>
        </motion.div>

        {/* Desktop: Show all testimonials */}
        <div className="hidden md:grid md:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => (
            <TestimonialCard 
              key={testimonial.id} 
              testimonial={testimonial} 
              isActive={true}
            />
          ))}
        </div>

        {/* Mobile: Carousel */}
        <div className="md:hidden">
          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.3 }}
              >
                <TestimonialCard 
                  testimonial={testimonials[currentIndex]} 
                  isActive={true}
                />
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              onClick={prevTestimonial}
              className="p-3 rounded-full border border-[#30363D] hover:border-[#007AFF] hover:bg-[#007AFF]/10 transition-colors"
              aria-label="Previous testimonial"
              data-testid="testimonial-prev-btn"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            
            {/* Dots */}
            <div className="flex gap-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentIndex 
                      ? 'w-6 bg-gradient-to-r from-[#007AFF] to-[#00C853]' 
                      : 'bg-[#30363D]'
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                  data-testid={`testimonial-dot-${index}`}
                />
              ))}
            </div>

            <button
              onClick={nextTestimonial}
              className="p-3 rounded-full border border-[#30363D] hover:border-[#007AFF] hover:bg-[#007AFF]/10 transition-colors"
              aria-label="Next testimonial"
              data-testid="testimonial-next-btn"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
